<template>
  <div class="autocomplete form-control">
    <textarea
      v-model="inputValue"
      class="autocomplete__input px-2 py-2 text-center no-resize no-print"
      @input="callOnInput"
      @keyup.down="selectNext()"
      @keyup.up="selectPrevious()"
      @keyup.esc="selectNone()"
      @focus="callOnFocus"
      @blur="callOnBlur"
    ></textarea>

    <div
      class="w-100 h-100 autocomplete__input text-center printElement px-2 py-3"
    >
      <span>{{ textQuestion }}</span>
    </div>

    <ul v-if="items.length && showList" class="autocomplete__list no-print">
      <li
        v-for="(item, index) in items"
        :key="index"
        @click="callOnSelect(item)"
        class="autocomplete__list__item"
        :class="{
          'autocomplete__list__item--active': item.id === cell.question_id,
        }"
      >
        <div class="d-flex justify-content-between gap-2">
          <span class="mr-2">{{ item.question }}</span>
          <div class="d-flex my-auto py-1">
            <i
              class="fas fa-edit mr-1"
              @click.stop="enableEditItem(item)"
              data-toggle="tooltip-edit"
              :title="$t('shared.actions.edit')"
            />
            <i
              class="fas fa-trash"
              @click.stop="deleteQuestion(item.id)"
              data-toggle="tooltip-delete"
              :title="$t('shared.actions.delete')"
            />
          </div>
        </div>
      </li>
    </ul>

    <ul
      v-else-if="inputValue && showList"
      class="autocomplete__list"
      @click="addQuestion"
    >
      <li class="autocomplete__list__item text-center">
        {{ $t('cryptogramGenerator.button.addQuestion') }}
      </li>
    </ul>

    <ul
      v-else-if="enableEditing"
      class="autocomplete__list"
      @click="editQuestion"
    >
      <li
        class="autocomplete__list__item text-center"
        @click.stop="cancelEdit()"
      >
        {{ $t('shared.actions.cancel') }}
      </li>
      <li class="autocomplete__list__item text-center">
        {{ $t('cryptogramGenerator.button.editQuestion') }}
      </li>
    </ul>
  </div>
</template>

<script>
import PuzzleService from '_services/rest/modules/puzzle';
import { mapActions } from 'vuex';
import Events from '_utils/events';
import { LEVEL } from '_utils/level';
import {
  _CRYPTOGRAM_CLEAR_QUESTION_,
  _CRYPTOGRAM_SET_QUESTION_,
} from './CryptogramModel';

export default {
  name: 'questions',
  props: {
    cell: {
      type: Object,
      require: true,
    },
    placeholder: {
      type: String,
      default: null,
    },
    parameter: {
      type: [String, Number, Object, Array],
      default: null,
    },
    itemValue: {
      type: String,
      default: null,
    },
    itemShow: {
      type: String,
      default: null,
    },
    state: {
      type: String,
      default: null,
    },
    onBlur: {
      type: Function,
      default: () => {},
    },
    onSelect: {
      type: Function,
      default: () => {},
    },
    completeInput: {
      type: Boolean,
      default: true,
    },
    clearInput: {
      type: Boolean,
      default: false,
    },
    closeModal: {
      type: Boolean,
      default: true,
    },
    onEnter: {
      type: Boolean,
      default: true,
    },
    onFocus: {
      type: Function,
      default: null,
    },
    line: {
      type: String,
    },
    level: {
      type: String,
      default: LEVEL.MEDIUM,
    },
  },
  data() {
    return {
      items: [],
      inputFocus: false,
      selectedIndex: -1,
      enableEditing: false,
      questionSelected: null,
      inputValue: '',
    };
  },
  computed: {
    showList() {
      return this.inputFocus && !this.enableEditing;
    },
    textQuestion() {
      if (this.cell.text) {
        return (
          (this.questionSelected && this.questionSelected.question) ||
          this.cell.text
        );
      }
      return '';
    },
  },
  methods: {
    ...mapActions(['setToggle', 'activeAlert']),
    resolveApiErrorMessage(error, fallbackKey) {
      if (error?.errors && typeof error.errors === 'object') {
        return Object.values(error.errors).flat().join('<br/> ');
      }

      if (error?.data && typeof error.data === 'object') {
        const values = Object.values(error.data);
        if (
          values.length &&
          values.every(v => Array.isArray(v) || typeof v === 'string')
        ) {
          return values.flat().join('<br/> ');
        }
      }

      if (typeof error?.message === 'string' && error.message) {
        return error.message;
      }

      return this.$t(fallbackKey);
    },
    getInputValue() {
      return (this.inputValue || '').trim();
    },
    getQuestions() {
      if (!this.cell.answer_id) {
        this.items = [];
        return;
      }
      PuzzleService.getQuestionsByAnswerId(this.cell.answer_id)
        .then(response => {
          this.items = Array.isArray(response) ? response : [];
        })
        .catch(() => {
          this.items = [];
        });
    },
    addQuestion() {
      const question = this.getInputValue();
      if (!this.cell.answer_id || !question) {
        return;
      }
      PuzzleService.saveQuestion({
        question,
        answer_id: this.cell.answer_id,
        size_id: 3,
        level: this.level || LEVEL.MEDIUM,
      })
        .then(response => {
          this.$emit('update-question', response);
          this.activeAlert({
            message: this.$t('cryptogramGenerator.alert.questionAdded'),
            theme: 'success',
          });
          this.inputFocus = false;
        })
        .catch(error => {
          this.activeAlert({
            message: this.resolveApiErrorMessage(
              error,
              'cryptogramGenerator.alert.questionSaveError',
            ),
            theme: 'danger',
          });
        });
    },
    deleteQuestion(id) {
      PuzzleService.deleteQuestion(id)
        .then(() => {
          this.inputValue = '';
          this.activeAlert({
            message: this.$t('cryptogramGenerator.alert.questionDeleted'),
            theme: 'success',
          });
          this.inputFocus = false;
        })
        .catch(error => {
          this.activeAlert({
            message: this.resolveApiErrorMessage(
              error,
              'cryptogramGenerator.alert.questionDeleteError',
            ),
            theme: 'danger',
          });
        });
    },
    callOnBlur() {
      this.onBlur(this.inputValue, this.parameter);
      setTimeout(() => {
        this.inputFocus = false;
      }, 200);
    },
    callOnInput() {
      const input = this.inputValue || '';
      if (input !== '') {
        const filteredTerms = this.items.filter(term =>
          term.question.toLowerCase().includes(input.toLowerCase()),
        );

        this.items = filteredTerms;
      }
      if (input === '' && !this.enableEditing) {
        this.getQuestions();
      }
    },
    callOnFocus() {
      if (!this.enableEditing) {
        this.getQuestions();
        this.inputFocus = true;
      }
    },
    callOnSelect(item) {
      this.inputValue = this.itemShow ? item[this.itemShow] : item;
      this.inputFocus = false;
      this.questionSelected = item;
      this.$emit('update-question', item);
      if (this.clearInput) {
        this.inputValue = '';
      }

      if (this.closeModal) {
        this.close();
      }
    },
    clear() {
      this.inputValue = '';
      this.close();
    },
    close() {
      this.items.splice(0, this.items.length);
      this.$set(this, 'selectedIndex', -1);
    },
    selectNext() {
      if (this.selectedIndex < this.items.length - 1) {
        this.selectedIndex += 1;
      }
    },
    selectPrevious() {
      if (this.selectedIndex > 0) {
        this.selectedIndex -= 1;
      }
    },
    selectNone() {
      if (this.selectedIndex > -1) {
        this.close();
      }
    },
    enableEditItem(item) {
      this.inputFocus = true;
      this.inputValue = item.question;
      this.enableEditing = true;
      this.questionSelected = item;
    },
    cancelEdit() {
      this.enableEditing = false;
      this.inputValue = this.questionSelected.question;
    },
    editQuestion() {
      const question = this.getInputValue();
      if (
        !this.questionSelected ||
        !this.questionSelected.answer_id ||
        !question
      ) {
        return;
      }

      PuzzleService.editQuestion({
        question,
        answer_id: this.questionSelected.answer_id,
        id: this.questionSelected.id,
        size_id: 3,
        level: this.questionSelected.level || LEVEL.MEDIUM,
      })
        .then(response => {
          this.$emit('update-question', response);
          this.activeAlert({
            message: this.$t('cryptogramGenerator.alert.questionEdited'),
            theme: 'success',
          });
          this.enableEditing = false;
          this.inputFocus = false;
        })
        .catch(error => {
          this.activeAlert({
            message: this.resolveApiErrorMessage(
              error,
              'cryptogramGenerator.alert.questionSaveError',
            ),
            theme: 'danger',
          });
        });
    },
  },
  mounted() {
    this.inputValue = this.cell.text || '';

    Events.$on(_CRYPTOGRAM_SET_QUESTION_, answer => {
      if (this.line === answer.line.toString()) {
        this.inputValue = (answer.question && answer.question.question) || '';
        this.questionSelected = answer.question || null;
      }
    });

    Events.$on(_CRYPTOGRAM_CLEAR_QUESTION_, line => {
      if (this.line && this.line === line.toString()) {
        this.inputValue = '';
        this.questionSelected = null;
        this.enableEditing = false;
      }
    });
  },
  watch: {
    'cell.text': function cellTextWatcher(value) {
      if (!this.inputFocus) {
        this.inputValue = value || '';
      }
    },
  },
  destroyed() {
    Events.$off(_CRYPTOGRAM_SET_QUESTION_);
    Events.$off(_CRYPTOGRAM_CLEAR_QUESTION_);
  },
};
</script>

<style lang="scss" scoped>
@import '~_scss/overrides';

.printElement {
  display: none;
  color: black;
}

.no-resize {
  resize: none;
}

.autocomplete__input {
  border: 0;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  width: 100%;
  height: 100%;
  line-height: 1;

  &:focus {
    box-shadow: none;
    border: 0;
  }
}

.autocomplete {
  position: relative;
  border: 0;
  box-shadow: none;
  padding: 0;
  &__list {
    width: 100%;
    padding: 0;

    position: absolute;
    z-index: 10;

    box-shadow: 2px 2px 2px lighten($primary, 25%);
    border: 1px solid $primary;
    border-radius: 5px;
    list-style: none;

    background-color: #fff;

    &__item {
      padding: 5px 15px;

      cursor: pointer;

      &:hover {
        background-color: lighten($primary, 15%);
        color: #fff;
      }

      &:not(:last-child) {
        border-bottom: 1px solid #ccc;
      }

      &--active {
        background-color: $primary;
        color: #fff;
      }
    }
  }
}
</style>
